import * as stylex from "@stylexjs/stylex";
import { forwardRef, Fragment } from "react";

import {
  numericPercentages,
  numericValues,
  radius,
  semanticColors,
  spacing,
} from "../../../../../global/stylex/vars.stylex";
import {
  type HallowElement,
  type HallowElementProps,
  type WithAsChild,
  type WithStylexArray,
} from "../../../../types";
import {
  determineChildrenInject,
  determineElementFromAsChild,
} from "../../../../utils";
import type { IconElement } from "../../icons";
import { ChevronRightIcon, PlaceholderIcon } from "../../icons";
import { Text } from "../Text";

const hallowElement: HallowElement = "div";

const styles = stylex.create({
  base: {
    alignItems: "center",
    backgroundColor: {
      default: semanticColors.background,
      ":hover": semanticColors.neutralsLowest,
      ":focus": semanticColors.neutralsLowest,
      ":active": semanticColors.neutralsLowest,
    },
    borderRadius: radius.m,
    color: semanticColors.primary,
    cursor: "pointer",
    display: "flex",
    gap: spacing.ms,
    justifyContent: "space-between",
    opacity: {
      default: numericValues[1],
      ":focus": numericValues[0.6],
      ":active": numericValues[0.6],
    },
    paddingBottom: spacing.s,
    paddingLeft: spacing.m,
    paddingRight: spacing.m,
    paddingTop: spacing.s,
    width: numericPercentages[100],
  },
  bible: {
    alignItems: "center",
    borderRadius: radius.full,
    display: "flex",
    flexDirection: "column",
    gap: spacing.xxs,
    height: spacing.xxxl,
    justifyContent: "center",
    width: spacing.xxxl,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    gap: spacing.xxs,
    width: spacing.t,
  },
  disabled: {
    backgroundColor: semanticColors.background,
    cursor: "not-allowed",
    opacity: numericValues[0.5],
  },
  metaData: {
    alignItems: "center",
    color: semanticColors.neutralsMedium,
    display: "flex",
    gap: spacing.xs,
  },
});

export type ListBibleProps = WithAsChild<
  WithStylexArray<HallowElementProps<typeof hallowElement>>
> & {
  book: string;
  chapter: number | string;
  description?: string;
  disabled?: boolean;
  icon?: IconElement;
  metaData?: string[];
  metaDataIcon?: IconElement;
  title: string;
};

export const ListBible = forwardRef<any, ListBibleProps>(
  (
    {
      asChild = false,
      book,
      chapter,
      children,
      disabled,
      icon = <ChevronRightIcon />,
      metaData,
      metaDataIcon = <PlaceholderIcon />,
      styleXArray = [],
      title,
      ...props
    },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        <span key="bible" {...stylex.props(styles.bible)}>
          <Text size="m" type="title">
            {book}
          </Text>
          <Text size="m" type="title">
            {chapter}
          </Text>
        </span>,
        <span key="content" {...stylex.props(styles.content)}>
          <Text overflow="ellipsis" size="l" type="quote">
            {title}
          </Text>
          {metaData?.length > 0 && (
            <span {...stylex.props(styles.metaData)}>
              {metaData.map((metaDataItem, index) => (
                <Fragment key={`${metaDataItem}${index}`}>
                  <Text overflow="ellipsis" size="l" type="detail">
                    {metaDataItem}
                  </Text>
                  {index < metaData.length - 1 && metaDataIcon}
                </Fragment>
              ))}
            </span>
          )}
        </span>,
        <Fragment key="icon">{icon}</Fragment>,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        ref={ref}
        {...stylex.props(
          styles.base,
          disabled ? styles.disabled : null,
          styleXArray,
        )}
        {...{ disabled, ...props }}
      >
        {DeterminedChildren}
      </DeterminedElement>
    );
  },
);

ListBible.displayName = "ListBible";
